'use client';

import { useState } from 'react';

import { usePathname } from 'next/navigation';

import { motion, AnimatePresence } from 'framer-motion';

import { useConsent } from '@/components/consent/consent-context';
import { Switch } from '@/components/ui/switch';
import { usePageLoaded } from '@/hooks/use-page-loaded';
import type { ConsentCategories } from '@/lib/consent';
import { getTranslation, type Locale } from '@/lib/i18n';
import { Link } from '@/lib/i18n/link';

/**
 * GDPR-grade consent banner. Only shown in opt-in jurisdictions (EEA/UK/CH or
 * unknown geo) until the visitor makes a choice; the preferences panel can be
 * reopened from anywhere (footer "Cookie settings") to change or withdraw
 * consent (Art. 7(3) GDPR). Accept and Reject have equal prominence.
 */
export function CookieConsent() {
  // Mounted in the root layout outside the [locale] segment's LocaleProvider,
  // so the locale is derived from the URL directly.
  const pathname = usePathname();
  const locale: Locale = pathname.split('/')[1] === 'ar' ? 'ar' : 'en';
  const t = getTranslation(locale);
  const isPageLoaded = usePageLoaded();

  const {
    consent,
    hasStoredChoice,
    optInRequired,
    isReady,
    isPreferencesOpen,
    closePreferences,
    saveConsent,
  } = useConsent();

  const [showCustomize, setShowCustomize] = useState(false);
  // Unsaved toggle edits; null means "mirror the effective consent". Reset to
  // null whenever the panel opens so it always starts from the current state.
  const [draft, setDraft] = useState<ConsentCategories | null>(null);
  const panelValues = draft ?? consent;

  const showBanner =
    isReady && isPageLoaded && optInRequired && !hasStoredChoice;
  const isVisible = showBanner || isPreferencesOpen;
  const showPanel = isPreferencesOpen || showCustomize;

  if (!isVisible) return null;

  const acceptAll = () => {
    setShowCustomize(false);
    setDraft(null);
    saveConsent({ analytics: true, marketing: true });
  };
  const rejectAll = () => {
    setShowCustomize(false);
    setDraft(null);
    saveConsent({ analytics: false, marketing: false });
  };
  const savePreferences = () => {
    setShowCustomize(false);
    setDraft(null);
    saveConsent(panelValues);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-4 z-50 ltr:right-4 rtl:left-4 md:bottom-16 md:ltr:right-6 md:rtl:left-6"
        role="dialog"
        aria-modal="false"
        aria-label={t.common['Cookie preferences']}
      >
        <div className="w-[calc(100vw-2rem)] max-w-sm">
          <div
            // The buttons are pills now, so their effective radius is half
            // their height — 16px on a 32px control. That is the source of
            // truth; the card follows it.
            style={{ '--inner-radius': '16px' } as React.CSSProperties}
            className="radius-concentric relative overflow-hidden border border-black/8 bg-white/90 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.12)] backdrop-blur-xl"
          >
            <div className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-black/10 to-transparent" />

            <div className="relative p-5">
              <p className="text-foreground/90 text-sm">
                {
                  t.common[
                    'We use cookies for analytics and marketing. You can accept all, reject all, or customize your choices.'
                  ]
                }{' '}
                <Link
                  href="/cookie-policy"
                  className="text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                >
                  {t.common['Cookie Policy']}
                </Link>
              </p>

              {showPanel && (
                <div className="mt-4 space-y-3 border-t border-black/5 pt-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-foreground text-xs font-medium">
                        {t.common['Strictly necessary']}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {
                          t.common[
                            'Needed for the site to work — language, region, and your cookie choices.'
                          ]
                        }
                      </p>
                    </div>
                    <Switch
                      checked
                      disabled
                      aria-label={t.common['Always on']}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-foreground text-xs font-medium">
                        {t.common['Analytics']}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {
                          t.common[
                            'Helps us understand how the site is used, including session replay.'
                          ]
                        }
                      </p>
                    </div>
                    <Switch
                      checked={panelValues.analytics}
                      onCheckedChange={(v) =>
                        setDraft({ ...panelValues, analytics: v })
                      }
                      aria-label={t.common['Analytics']}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-foreground text-xs font-medium">
                        {t.common['Marketing']}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {
                          t.common[
                            'Advertising and visitor identification by our marketing partners.'
                          ]
                        }
                      </p>
                    </div>
                    <Switch
                      checked={panelValues.marketing}
                      onCheckedChange={(v) =>
                        setDraft({ ...panelValues, marketing: v })
                      }
                      aria-label={t.common['Marketing']}
                    />
                  </div>
                </div>
              )}

              <div className="mt-4 flex items-center justify-between gap-2">
                {showPanel ? (
                  <button
                    onClick={savePreferences}
                    className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 transition-colors hover:underline"
                  >
                    {t.common['Save preferences']}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setDraft(null);
                      setShowCustomize(true);
                    }}
                    className="text-muted-foreground hover:text-foreground text-xs underline-offset-2 transition-colors hover:underline"
                  >
                    {t.common['Customize']}
                  </button>
                )}
                {/* Reject and Accept: identical size and weight — reject
                    parity is a legal requirement, not a design choice. */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={rejectAll}
                    className="border-foreground/20 text-foreground hover:bg-foreground/5 rounded-full border px-4 py-2 text-xs font-medium transition-all active:scale-[0.98]"
                  >
                    {t.common['Reject all']}
                  </button>
                  <button
                    onClick={acceptAll}
                    className="bg-foreground text-background hover:bg-foreground/90 rounded-full px-4 py-2 text-xs font-medium transition-all hover:shadow-md active:scale-[0.98]"
                  >
                    {t.common['Accept all']}
                  </button>
                </div>
              </div>

              {isPreferencesOpen && (
                <button
                  onClick={() => {
                    setShowCustomize(false);
                    setDraft(null);
                    closePreferences();
                  }}
                  className="text-muted-foreground hover:text-foreground mt-3 text-xs underline-offset-2 transition-colors hover:underline"
                >
                  {t.common.Close}
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
